import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Instagram, Clock, Loader2, TrendingUp, Sparkles, Send,
  Heart, MessageSquare, Share2, X, FileText, CheckCircle
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const InstagramManagerPage = () => {
  const [stats, setStats] = useState(null);
  const [posts, setPosts] = useState([]);
  const [logs, setLogs] = useState([]);
  const [latestTrend, setLatestTrend] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [researching, setResearching] = useState(false);
  const [publishing, setPublishing] = useState(null);
  const [selectedPost, setSelectedPost] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [statsRes, postsRes, logsRes] = await Promise.all([
        axios.get(`${API}/instagram/dashboard-stats`),
        axios.get(`${API}/instagram/posts`),
        axios.get(`${API}/instagram/logs?limit=20`)
      ]);
      setStats(statsRes.data.stats);
      setLatestTrend(statsRes.data.latest_trend);
      setPosts(postsRes.data.posts || []);
      setLogs(logsRes.data.logs || []);
    } catch (error) {
      toast.error('Fehler beim Laden der Daten');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleResearchTrends = async () => {
    setResearching(true);
    try {
      const response = await axios.post(`${API}/instagram/research-trends`);
      setLatestTrend(response.data.data);
      toast.success('Trends recherchiert!');
      fetchData();
    } catch (error) {
      toast.error('Fehler: ' + (error.response?.data?.detail || error.message));
    } finally {
      setResearching(false);
    }
  };

  const handleGeneratePost = async () => {
    setGenerating(true);
    try {
      const response = await axios.post(`${API}/instagram/generate-post`);
      toast.success('Post generiert!');
      setSelectedPost(response.data.post);
      fetchData();
    } catch (error) {
      toast.error('Fehler: ' + (error.response?.data?.detail || error.message));
    } finally {
      setGenerating(false);
    }
  };

  const handlePublishPost = async (postId) => {
    setPublishing(postId);
    try {
      await axios.post(`${API}/instagram/post?post_id=${postId}`);
      toast.success('Post veröffentlicht!');
      fetchData();
    } catch (error) {
      toast.error('Fehler: ' + (error.response?.data?.detail || error.message));
    } finally {
      setPublishing(null);
    }
  };

  const handleScheduleDaily = async () => {
    try {
      const response = await axios.post(`${API}/instagram/schedule-daily`);
      toast.success(response.data.message);
      fetchData();
    } catch (error) {
      toast.error('Fehler: ' + (error.response?.data?.detail || error.message));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-fox-fire" />
      </div>
    );
  }

  return (
    <div data-testid="instagram-page" className="space-y-6 animate-fade-in">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold text-stone-900 flex items-center gap-3">
            <Instagram className="w-8 h-8 text-pink-500" />
            Instagram Manager
          </h1>
          <p className="text-stone-500 mt-1">Riona AI - Automatisierte Posts für Förderfuchs</p>
        </div>
        <button onClick={handleScheduleDaily} className="btn-primary flex items-center gap-2">
          <Clock size={18} />
          Daily Post planen (10:00)
        </button>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl border border-stone-200 p-4">
          <div className="text-3xl font-bold text-stone-900">{stats?.total_posts || 0}</div>
          <div className="text-sm text-stone-500">Gesamt Posts</div>
        </div>
        <div className="bg-white rounded-xl border border-stone-200 p-4">
          <div className="text-3xl font-bold text-green-600">{stats?.published || 0}</div>
          <div className="text-sm text-stone-500">Veröffentlicht</div>
        </div>
        <div className="bg-white rounded-xl border border-stone-200 p-4">
          <div className="text-3xl font-bold text-amber-500">{stats?.scheduled || 0}</div>
          <div className="text-sm text-stone-500">Geplant</div>
        </div>
        <div className="bg-white rounded-xl border border-stone-200 p-4">
          <div className="text-3xl font-bold text-stone-400">{stats?.drafts || 0}</div>
          <div className="text-sm text-stone-500">Entwürfe</div>
        </div>
      </div>

      {/* Workflow Steps */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Step 1 */}
        <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl border border-purple-200 p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center text-purple-600 font-bold">1</div>
            <div>
              <h3 className="font-heading font-bold">Trends recherchieren</h3>
              <p className="text-sm text-stone-500">Mit KI nach Trends suchen</p>
            </div>
          </div>
          <button
            data-testid="research-trends-btn"
            onClick={handleResearchTrends}
            disabled={researching}
            className="w-full btn-primary bg-purple-500 hover:bg-purple-600 flex items-center justify-center gap-2"
          >
            {researching ? <Loader2 className="w-4 h-4 animate-spin" /> : <TrendingUp size={18} />}
            {researching ? 'Recherchiere...' : 'Trends finden'}
          </button>

          {latestTrend && (
            <div className="mt-4 p-3 bg-white rounded-lg text-sm">
              <div className="font-medium text-purple-700">{latestTrend.topic}</div>
              <div className="flex flex-wrap gap-1 mt-2">
                {latestTrend.hashtags?.slice(0, 5).map((tag, i) => (
                  <span key={i} className="px-2 py-0.5 bg-purple-100 text-purple-700 rounded text-xs">{tag}</span>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Step 2 */}
        <div className="bg-gradient-to-br from-orange-50 to-amber-50 rounded-xl border border-orange-200 p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-fox-fire/20 rounded-full flex items-center justify-center text-fox-fire font-bold">2</div>
            <div>
              <h3 className="font-heading font-bold">Post generieren</h3>
              <p className="text-sm text-stone-500">KI erstellt Instagram Post</p>
            </div>
          </div>
          <button
            data-testid="generate-post-btn"
            onClick={handleGeneratePost}
            disabled={generating}
            className="w-full btn-primary flex items-center justify-center gap-2"
          >
            {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles size={18} />}
            {generating ? 'Generiere...' : 'Post erstellen'}
          </button>
        </div>

        {/* Step 3 */}
        <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl border border-green-200 p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center text-green-600 font-bold">3</div>
            <div>
              <h3 className="font-heading font-bold">Veröffentlichen</h3>
              <p className="text-sm text-stone-500">Auf Instagram posten</p>
            </div>
          </div>
          <div className="text-center text-sm text-stone-500">
            Wähle einen Post aus der Liste unten
          </div>
        </div>
      </div>

      {/* Generated Post Preview */}
      {selectedPost && (
        <div className="bg-white rounded-xl border border-stone-200 p-6 animate-fade-in">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-heading text-lg font-bold">Generierter Post</h3>
            <button onClick={() => setSelectedPost(null)} className="p-1 hover:bg-stone-100 rounded">
              <X size={18} />
            </button>
          </div>
          <div className="flex gap-6">
            {/* Instagram Phone Preview */}
            <div className="w-80 bg-stone-100 rounded-3xl p-4 flex-shrink-0">
              <div className="bg-white rounded-2xl overflow-hidden shadow-lg">
                <div className="flex items-center gap-3 p-3 border-b">
                  <div className="w-8 h-8 bg-fox-fire rounded-full flex items-center justify-center text-white text-sm">🦊</div>
                  <span className="font-semibold text-sm">foerderfuchs_bildung</span>
                </div>
                <div className="aspect-square bg-gradient-to-br from-fox-fire/20 to-amber-100 flex items-center justify-center">
                  <div className="text-center p-4">
                    <Instagram className="w-12 h-12 text-fox-fire/50 mx-auto mb-2" />
                    <p className="text-xs text-stone-500">{selectedPost.image_prompt?.substring(0, 50)}...</p>
                  </div>
                </div>
                <div className="p-3 flex items-center gap-4">
                  <Heart size={24} className="text-stone-700" />
                  <MessageSquare size={24} className="text-stone-700" />
                  <Share2 size={24} className="text-stone-700" />
                </div>
                <div className="px-3 pb-3">
                  <p className="text-sm whitespace-pre-wrap line-clamp-4">{selectedPost.caption?.substring(0, 200)}...</p>
                </div>
              </div>
            </div>

            {/* Full Caption */}
            <div className="flex-1">
              <h4 className="font-medium mb-2">Vollständiger Text:</h4>
              <div className="p-4 bg-stone-50 rounded-lg text-sm whitespace-pre-wrap max-h-80 overflow-y-auto">
                {selectedPost.caption}
              </div>
              <div className="mt-4 flex flex-wrap gap-2">
                {selectedPost.hashtags?.map((tag, i) => (
                  <span key={i} className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs">{tag}</span>
                ))}
              </div>
              <div className="mt-4 flex gap-3">
                <button
                  onClick={() => handlePublishPost(selectedPost.id)}
                  disabled={publishing === selectedPost.id}
                  className="btn-primary flex items-center gap-2"
                >
                  {publishing === selectedPost.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send size={18} />}
                  Jetzt veröffentlichen
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Posts List */}
      <div className="bg-white rounded-xl border border-stone-200 p-6">
        <h3 className="font-heading text-lg font-bold mb-4">Alle Posts</h3>
        {posts.length === 0 ? (
          <div className="text-center py-8 text-stone-500">
            <Instagram className="w-12 h-12 mx-auto mb-2 opacity-50" />
            <p>Noch keine Posts erstellt</p>
          </div>
        ) : (
          <div className="space-y-3">
            {posts.map((post) => (
              <div
                key={post.id}
                onClick={() => setSelectedPost(post)}
                className="flex items-center justify-between p-4 bg-stone-50 rounded-lg hover:bg-stone-100 cursor-pointer transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-500 rounded-lg flex items-center justify-center">
                    <Instagram size={18} className="text-white" />
                  </div>
                  <div>
                    <p className="font-medium text-stone-900 truncate max-w-md">{post.topic}</p>
                    <p className="text-sm text-stone-500 truncate max-w-md">{post.caption?.substring(0, 60)}...</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                    post.status === 'published' ? 'bg-green-100 text-green-700' :
                    post.status === 'scheduled' ? 'bg-amber-100 text-amber-700' :
                    'bg-stone-200 text-stone-600'
                  }`}>
                    {post.status === 'published' ? 'Veröffentlicht' :
                     post.status === 'scheduled' ? 'Geplant' : 'Entwurf'}
                  </span>
                  {post.status === 'draft' && (
                    <button
                      onClick={(e) => { e.stopPropagation(); handlePublishPost(post.id); }}
                      disabled={publishing === post.id}
                      className="p-2 hover:bg-green-100 rounded-lg text-green-600"
                    >
                      {publishing === post.id ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Activity Logs */}
      <div className="bg-white rounded-xl border border-stone-200 p-6">
        <h3 className="font-heading text-lg font-bold mb-4">Aktivitäts-Log</h3>
        {logs.length === 0 ? (
          <p className="text-stone-500 text-center py-4">Keine Aktivitäten</p>
        ) : (
          <div className="space-y-2 max-h-60 overflow-y-auto">
            {logs.map((log, index) => (
              <div key={index} className="flex items-center gap-3 text-sm p-2 rounded hover:bg-stone-50">
                <div className={`w-2 h-2 rounded-full ${log.status === 'success' ? 'bg-green-500' : 'bg-red-500'}`} />
                <span className="font-mono text-stone-400 text-xs">
                  {new Date(log.created_at).toLocaleString('de-DE')}
                </span>
                <span className="font-medium">{log.action_type}</span>
                <span className={log.status === 'success' ? 'text-green-600' : 'text-red-600'}>
                  {log.status}
                </span>
                {log.error && <span className="text-red-500 text-xs">{log.error}</span>}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default InstagramManagerPage;
